import {
  Button,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  RadioGroup,
  RadioGroupItem,
  Label,
  Alert,
  AlertDescription,
} from '@predoc/cow';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, XCircle } from 'lucide-react';
import { DatePickerInput } from '@/components/DatePickerInput';
import {
  DataDeletionRequestLevel,
  DataDeletionFormState,
  SelectedPatient,
  SelectedProfile,
} from '../types';
import { selectionStepSchema, SelectionStepFormType } from '../validation';
import { PatientSelectionTable } from './PatientSelectionTable';
import { ProfileSelectionTable } from './ProfileSelectionTable';

interface SelectionStepProps {
  formState: DataDeletionFormState;
  setFormState: React.Dispatch<React.SetStateAction<DataDeletionFormState>>;
  onCancel: () => void;
  onNext: () => void;
  totalPatientCount: number;
}

export const SelectionStep = ({
  formState,
  setFormState,
  onCancel,
  onNext,
  totalPatientCount,
}: SelectionStepProps) => {
  const form = useForm<SelectionStepFormType>({
    resolver: zodResolver(selectionStepSchema),
    defaultValues: {
      deletionType: formState.deletionType,
      deletionDate: formState.deletionDate,
      selectedPatients: formState.selectedPatients.map((p) => p.id),
      selectedProfiles: formState.selectedProfiles.map((p) => p.id),
    },
  });

  const watchedDeletionType = form.watch('deletionType');

  const handleDeletionTypeChange = (value: string) => {
    form.setValue('deletionType', value as DataDeletionRequestLevel);
    setFormState((prev) => ({
      ...prev,
      deletionType: value as DataDeletionRequestLevel,
    }));
  };

  const handleDateChange = (date: Date | undefined) => {
    form.setValue('deletionDate', date as Date, {
      shouldValidate: form.formState.isSubmitted,
    });
    setFormState((prev) => ({
      ...prev,
      deletionDate: date,
    }));
  };

  const handlePatientSelectionChange = (patients: SelectedPatient[]) => {
    setFormState((prev) => ({
      ...prev,
      selectedPatients: patients,
    }));
    form.setValue(
      'selectedPatients',
      patients.map((p) => p.id),
      {
        shouldValidate: form.formState.isSubmitted,
      },
    );
  };

  const handleProfileSelectionChange = (profiles: SelectedProfile[]) => {
    setFormState((prev) => ({
      ...prev,
      selectedProfiles: profiles,
    }));
    form.setValue(
      'selectedProfiles',
      profiles.map((p) => p.id),
      {
        shouldValidate: form.formState.isSubmitted,
      },
    );
  };

  const handleNext = form.handleSubmit(() => {
    onNext();
  });

  const dateError = form.formState.errors.deletionDate?.message;
  const selectionError =
    form.formState.errors.selectedPatients?.message ||
    form.formState.errors.selectedProfiles?.message;
  const hasValidationErrors =
    form.formState.isSubmitted && Object.keys(form.formState.errors).length > 0;

  return (
    <>
      <div className="flex-1 overflow-y-auto px-6">
        <Form {...form}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="mb-1 block text-sm font-normal">
                What patient PHI & PII data do you need to delete? *
              </Label>
              <RadioGroup
                value={watchedDeletionType}
                onValueChange={handleDeletionTypeChange}
                className="!gap-1"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={DataDeletionRequestLevel.SPECIFIC_PATIENTS}
                    id="specific-patients"
                    className="radio-clean"
                  />
                  <Label
                    htmlFor="specific-patients"
                    className="cursor-pointer text-sm font-normal"
                  >
                    Data for only specific patients
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={DataDeletionRequestLevel.SPECIFIC_PROFILES}
                    id="specific-profiles"
                    className="radio-clean"
                  />
                  <Label
                    htmlFor="specific-profiles"
                    className="cursor-pointer text-sm font-normal"
                  >
                    Data for all patients within specific profiles
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={DataDeletionRequestLevel.ENTIRE_ORGANIZATION}
                    id="all-patients"
                    className="radio-clean"
                  />
                  <Label
                    htmlFor="all-patients"
                    className="cursor-pointer text-sm font-normal"
                  >
                    Data for all patients
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <FormField
              control={form.control}
              name="deletionDate"
              render={() => (
                <FormItem>
                  <FormLabel className="text-sm font-normal">
                    When do you need it deleted by? *
                  </FormLabel>
                  <FormControl>
                    <DatePickerInput
                      value={formState.deletionDate}
                      onChange={handleDateChange}
                      placeholder="Pick a date"
                      disableDates="past"
                      error={!!dateError}
                    />
                  </FormControl>
                  <FormMessage />
                  <p className="text-muted-foreground mt-1 text-sm">
                    A member of our Predoc team will reach out within a few
                    business days.
                  </p>
                </FormItem>
              )}
            />
          </div>

          <div className="mt-8">
            {watchedDeletionType ===
            DataDeletionRequestLevel.SPECIFIC_PATIENTS ? (
              <PatientSelectionTable
                selectedPatients={formState.selectedPatients}
                onSelectionChange={handlePatientSelectionChange}
                validationError={selectionError}
              />
            ) : null}

            {watchedDeletionType ===
            DataDeletionRequestLevel.SPECIFIC_PROFILES ? (
              <ProfileSelectionTable
                selectedProfiles={formState.selectedProfiles}
                onSelectionChange={handleProfileSelectionChange}
                validationError={selectionError}
              />
            ) : null}
            {watchedDeletionType ===
            DataDeletionRequestLevel.ENTIRE_ORGANIZATION ? (
              <Alert variant="accent">
                <AlertCircle className="size-4" />
                <AlertDescription>
                  This will delete data for all ({totalPatientCount}) patients.
                </AlertDescription>
              </Alert>
            ) : null}
          </div>
        </Form>
      </div>

      <div className="flex items-center justify-between border-t px-6 py-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <div className="flex items-center gap-3">
          {hasValidationErrors ? (
            <div className="text-destructive flex items-center gap-1 text-sm">
              <XCircle className="size-4" />
              Please fix validation errors.
            </div>
          ) : null}
          <Button onClick={handleNext}>Next</Button>
        </div>
      </div>
    </>
  );
};
